import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ArrowRight, CalendarDays, Download, History, Inbox, Layers, Plus, Scale, Search, Trash2, Undo2, UserPlus, Users, WandSparkles, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TooltipProvider } from '../components/ui/tooltip';
import { createEventContactsApi } from '../lib/eventContactsApi';
import { eventEnrollmentOutcomeMessages } from '../lib/eventEnrollmentOutcomes';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MAX_SOURCE_COLUMNS, createLmApi, lmError, lmErrorCode } from '../components/list-management/lmApi';
import useLmQueryState, { TAB_VIEW, rowParams, selectionContextKey } from '../components/list-management/useLmQueryState';
import useLmRows from '../components/list-management/useLmRows';
import LmTable, { SOURCE_ICONS } from '../components/list-management/LmTable';
import LmColumnPicker from '../components/list-management/LmColumnPicker';
import LmBulkDialog, { OwnerSelect } from '../components/list-management/LmBulkDialog';
import LmRetention from '../components/list-management/LmRetention';
import LmImportWizard from '../components/list-management/LmImportWizard';
import LmRowDrawer from '../components/list-management/LmRowDrawer';
import LmCommitDialog from '../components/list-management/LmCommitDialog';
import LmEnrichDialog from '../components/list-management/LmEnrichDialog';
import LmHistoryView, { DeletedImports } from '../components/list-management/LmHistoryView';
import LmResolveDialog, { RESOLVE_MAX_ROWS } from '../components/list-management/LmResolveDialog';
import { RepBalance, RepDistribution } from '../components/list-management/LmPooling';
import {
    CountPill, DECOR_TEXT, GhostButton, NoCrmWriteBanner, PrimaryButton, ReadOnlyBanner, toneClasses,
} from '../components/list-management/LmBits';
import {
    MATCH_LABELS, SOURCE_META, blockLabel, clearColumns, columnsFor, deletedColumns, displayName, idsThatLeft,
    readColumnPrefs, resetColumns, showAllColumns, toggleColumn, visibleColumns, writeColumnPrefs,
} from '../components/list-management/lmColumns';

/**
 * CRM → Insights → List Management (Epic #767).
 *
 * Imports (read-only) → Staging Area (editable) → For Pooling (pre-CRM) →
 * Admin-confirmed Commit. Layout, labels and copy follow the design prototype
 * (Inbox.dc.html, `isList` / `importOpen`); behaviour follows the product
 * contract in docs/runtime/current/list-management.md.
 */
const TAB_SPEC = [
    { key: 'imports', label: 'Imports', icon: Download, count: 'imports' },
    { key: 'staging', label: 'Staging Area', icon: Layers, count: 'staging' },
    { key: 'pool', label: 'For Pooling', icon: Inbox, count: 'pool' },
    { key: 'history', label: 'Import History', icon: History },
    { key: 'deleted', label: 'Deleted', icon: Trash2 },
];
const SEARCH_PLACEHOLDER = {
    imports: 'Search contacts or companies...', staging: 'Search contacts or companies...',
    pool: 'Search pooled contacts...', deleted: 'Search deleted records...',
};
const RUNTIME_API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const EMPTY_LABEL = {
    imports: 'No imported records found', staging: 'No staging records found',
    pool: 'No pooled records found', deleted: 'No deleted records found',
};
const ENRICH_MAX = 25;

function FilterSelect({ label, value, onChange, options }) {
    return (
        <label className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2">
            <span className="sr-only sm:not-sr-only">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
                    className="h-[30px] px-2 border border-input rounded-lg bg-card text-[12.5px] text-foreground">
                <option value="">All</option>
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}

/**
 * #975: the one TooltipProvider for every List Management tooltip (headers,
 * badges, the row drawer, Import History and the Columns picker). React context
 * reaches through Radix portals, so the drawer and picker are covered too.
 */
export default function ListManagement() {
    return (
        <TooltipProvider delayDuration={300} skipDelayDuration={150}>
            <ListManagementPage />
        </TooltipProvider>
    );
}

function ListManagementPage() {
    const { getAuthHeader, user } = useAuth();
    const { can, permissionsUnknown, hasEffectivePermission } = usePermissions();
    // getAuthHeader changes identity on every token refresh; an `api` keyed on it
    // would re-run every effect (reset the wizard, re-preview a commit, drop edits).
    const authRef = useRef(getAuthHeader);
    authRef.current = getAuthHeader;
    const api = useMemo(() => createLmApi(() => (authRef.current ? authRef.current() : {})), []);
    const { state, update, setTab } = useLmQueryState();
    const [summary, setSummary] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [owners, setOwners] = useState([]);
    const [fieldLabels, setFieldLabels] = useState({});
    // The import field catalog (#974): every CRM field and custom field the Columns
    // picker can offer. Null for a review-only user (the route needs `import`),
    // who keeps the 20 known columns.
    const [catalog, setCatalog] = useState(null);
    // The imports the rows on screen came from (`rows.batches`, copied after each
    // response). The columns need them to pick what is on by default, and the
    // rows request needs the columns (for the file columns' values), so they cannot
    // be read straight off the hook that this feeds.
    const [viewBatches, setViewBatches] = useState({});
    const [selected, setSelected] = useState(() => new Set());
    const [allMatching, setAllMatching] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [drawerRowId, setDrawerRowId] = useState(null);
    const [bulk, setBulk] = useState(null);
    const [assignOwner, setAssignOwner] = useState('');
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    // The selection a dialog acts on is frozen when it opens: the page clears
    // its live selection after a commit, and a dialog re-reading it would
    // re-run its preview against nothing.
    const [commitSelection, setCommitSelection] = useState(null);
    const [enrichIds, setEnrichIds] = useState(null);
    const [resolveIds, setResolveIds] = useState(null);
    // Per-tab column overrides. Derived while rendering (never in an effect) so the
    // first frame of a new tab already uses that tab's own choices.
    const [prefsByTab, setPrefsByTab] = useState({});
    const prefs = useMemo(() => prefsByTab[state.tab] || readColumnPrefs(state.tab), [prefsByTab, state.tab]);

    // List Management rows are pre-CRM staging records, so this picker only
    // searches existing visible contacts and never creates a contact from a row.
    const [eventEnrollOpen, setEventEnrollOpen] = useState(false);
    const [eventEnrollEvents, setEventEnrollEvents] = useState([]);
    const [eventEnrollContacts, setEventEnrollContacts] = useState([]);
    const [eventEnrollSearch, setEventEnrollSearch] = useState('');
    const [eventEnrollEventId, setEventEnrollEventId] = useState('');
    const [eventEnrollSelected, setEventEnrollSelected] = useState(() => new Set());
    const [eventEnrollLoading, setEventEnrollLoading] = useState(false);
    const eventContactsApi = useMemo(() => createEventContactsApi(getAuthHeader), [getAuthHeader]);
    const tablistRef = useRef(null);

    const allow = useCallback((action) => !permissionsUnknown && can(`list_management:${action}`), [can, permissionsUnknown]);
    // The one-time Contact Owner migration (#862): an EFFECTIVE permission only —
    // `can()` also honours legacy grants and would over-show it.
    const canMigrateOwners = !permissionsUnknown && hasEffectivePermission('contacts:migrate_owner');
    // Rolling a migration (or one part) back is a restore: the server also requires this.
    const canRestoreOwner = !permissionsUnknown && hasEffectivePermission('contacts:restore_owner');
    const rowsTab = ['imports', 'staging', 'pool', 'deleted'].includes(state.tab) && !(state.tab === 'pool' && state.sub !== 'contacts');
    const tableView = rowsTab || state.tab === 'history';
    // Selection is scoped to one context: tab + source + filters + search. The same
    // key says which import population the batches (and so the columns) belong to.
    const contextKey = selectionContextKey(state);
    const importsInView = useMemo(() => Object.values(viewBatches), [viewBatches]);
    // #864: a view filtered to ONE owner-migration import labels its owner columns
    // "Current / New Contact Owner"; every other view keeps "Owner".
    const ownerMigration = !!state.imp && viewBatches?.[state.imp]?.mode === 'owner_migration';
    const tableColumns = useMemo(() => (state.tab === 'deleted' ? [] : columnsFor(state.tab, { ownerMigration, catalog, importsInView })),
        [state.tab, ownerMigration, catalog, importsInView]);
    const shownTableColumns = useMemo(() => visibleColumns(tableColumns, prefs), [tableColumns, prefs]);
    // Only the visible file columns are asked for: their values are not in a rows
    // page unless named. The server takes at most MAX_SOURCE_COLUMNS, so a table
    // with more says so (below) instead of showing the rest as empty cells.
    const allSourceHeaders = useMemo(() => shownTableColumns.filter((c) => c.isFile).map((c) => c.header), [shownTableColumns]);
    const sourceColumnsCapped = allSourceHeaders.length > MAX_SOURCE_COLUMNS;
    const sourceHeaders = useMemo(() => allSourceHeaders.slice(0, MAX_SOURCE_COLUMNS), [allSourceHeaders]);
    const rowsParams = useMemo(
        () => ({ ...rowParams(state), ...(sourceHeaders.length ? { source_columns: sourceHeaders } : {}) }),
        [state, sourceHeaders],
    );
    const rows = useLmRows(api, rowsParams, { enabled: rowsTab, batchContext: contextKey });
    useEffect(() => { setViewBatches(rows.batches || {}); }, [rows.batches]);
    const refresh = useCallback(() => { rows.refetch(); setRefreshKey((k) => k + 1); }, [rows]);
    // Moving or deleting every row of the last page leaves it empty: step back to
    // the real last page instead of showing "no records" beside a non-zero count.
    useEffect(() => {
        if (rows.status === 'ok' && !rows.loading && rows.total > 0 && (rows.data || []).length === 0 && state.page > 1) {
            update({ page: Math.max(1, Math.ceil(rows.total / state.size)) }, { resetPage: false });
        }
    }, [rows.status, rows.loading, rows.total, rows.data, state.page, state.size, update]);

    useEffect(() => {
        api.summary().then(setSummary).catch(() => setSummary(null));
    }, [api, refreshKey]);
    // The catalog is read again whenever the wizard may have created a custom field
    // (an import finished, or the wizard closed): without that the new column is
    // absent from the table and the Columns picker until a reload. A refusal (a
    // review-only user cannot read it) keeps whatever catalog is already held.
    const loadCatalog = useCallback(() => api.fields().then((cat) => {
        setCatalog(cat);
        const labels = {};
        ['company_fields', 'contact_fields', 'relationship_fields'].forEach((g) => (cat[g] || []).forEach((f) => { labels[f.key] = f.label; }));
        setFieldLabels(labels);
    }).catch(() => {}), [api]);
    useEffect(() => {
        api.owners().then((r) => setOwners(r.owners || [])).catch(() => setOwners([]));
        loadCatalog();
    }, [api, loadCatalog]);

    useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [contextKey]);

    const deletedCols = useMemo(() => deletedColumns(rows.batches), [rows.batches]);
    const columns = state.tab === 'deleted' ? deletedCols : shownTableColumns;
    const allColumns = state.tab === 'deleted' ? deletedCols : tableColumns;
    const visibleColumnIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns]);
    const applyColumnPrefs = (next) => {
        setPrefsByTab((p) => ({ ...p, [state.tab]: next }));
        writeColumnPrefs(state.tab, next);
    };

    const pageSelectable = (rows.data || []).filter((r) => !r.locked);
    const labelById = useMemo(() => Object.fromEntries((rows.data || []).map((r) => [r.row_id, displayName(r)])), [rows.data]);
    const labelFor = useCallback((id) => labelById[id] || id, [labelById]);

    // Leaving "all matching" mode starts from what the user sees selected — this
    // page — never from a stale selection kept from another page.
    const baseSelection = (s) => (allMatching ? new Set(pageSelectable.map((r) => r.row_id)) : new Set(s));
    const toggleRow = (id) => {
        setSelected((s) => { const n = baseSelection(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
        setAllMatching(false);
    };
    const toggleMany = (ids, on) => {
        setSelected((s) => { const n = baseSelection(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
        setAllMatching(false);
    };

    const view = TAB_VIEW[state.tab];
    const selectionBody = () => {
        if (allMatching) {
            const p = rowParams(state);
            // On Imports only `imported` rows are actionable: the selector must not
            // reach the locked rows that already moved to Staging or Pooling.
            return { selector: { view, search: p.search, expected_total: matchingTotal,
                                 filters: { source: p.source, import_id: p.import_id, entity_type: p.entity_type,
                                            match_status: p.match_status, validation_status: p.validation_status, owner: p.owner,
                                            ...(state.tab === 'imports' ? { state: 'imported' } : {}) } } };
        }
        return { row_ids: [...selected] };
    };
    const matchingTotal = rows.selectableTotal ?? rows.total;
    const selectedCount = allMatching ? matchingTotal : selected.size;
    const pageFullySelected = pageSelectable.length > 0 && pageSelectable.every((r) => selected.has(r.row_id));

    const openBulk = (kind) => setBulk({ kind, selection: selectionBody(), count: selectedCount });
    // Every bulk call names the tab it was taken from, so the server refuses any
    // row that has left it (`not_in_view`) instead of acting on it unseen.
    const runBulk = async (action, sel, extra = {}) => api.bulk(action, { ...sel, view, ...extra });
    // After a partial result, keep selected only the rows still actionable here
    // (#770 QA: rows a partial Move to Pool took to For Pooling stayed selected,
    // and Delete in Staging then removed them). A full success clears it.
    const settleSelection = (res) => {
        const results = res?.results || [];
        const left = idsThatLeft(results, state.tab);
        if (allMatching) {
            setSelected(new Set(results.map((r) => r.row_id).filter((id) => !left.has(id))));
            setAllMatching(false);
        } else {
            setSelected((s) => new Set([...s].filter((id) => !left.has(id))));
        }
    };
    const afterBulk = (res, verb) => {
        refresh();
        if (res?.failed === 0) {
            toast.success(`${res.succeeded} record${res.succeeded === 1 ? '' : 's'} ${verb}`);
            setSelected(new Set()); setAllMatching(false);
        } else if (res) {
            settleSelection(res);
        }
    };

    const selectionChanged = () => {
        setSelected(new Set()); setAllMatching(false); refresh();
        toast.error('The matching records changed since you selected them. Select them again.');
    };

    // Import History → a batch's rows in one view, filtered to that import (#775 QA).
    const openImportRows = (tab, importId) => update({
        tab, imp: importId, sub: 'contacts', source: 'all', q: '', sort: '', order: '',
        entity: '', match: '', validation: '', owner: '', bstatus: '',
    });
    const importFilterLabel = state.imp ? (rows.batches?.[state.imp]?.batch_name || state.imp) : '';

    const quick = async (action, verb, title) => {
        // Moves run immediately (prototype behaviour); partial failures open the outcome dialog.
        if (busyRef.current) return;  // a double-click must not send the move twice
        busyRef.current = true;
        setBusy(true);
        const sel = selectionBody();
        try {
            const res = await runBulk(action, sel);
            refresh();
            if (res.failed) {
                settleSelection(res);
                setBulk({ kind: 'outcome', title, action, verb, selection: sel, count: selectedCount, initial: res });
            }
            else {
                const skipped = action === 'move-to-pool' ? (res.results || []).filter((r) => r.state === 'skipped').length : 0;
                const moved = res.succeeded - skipped;
                toast.success(skipped
                    ? `${moved} record${moved === 1 ? '' : 's'} ${verb} · ${skipped} skipped as duplicate${skipped === 1 ? '' : 's'}`
                    : `${res.succeeded} record${res.succeeded === 1 ? '' : 's'} ${verb}`);
                setSelected(new Set()); setAllMatching(false);
            }
        } catch (err) {
            if (lmErrorCode(err) === 'selection_changed') selectionChanged();
            else toast.error(lmError(err, 'That action failed.'));
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    const sourceBase = state.tab === 'staging' ? 'staging' : 'imports';
    const sourceCount = (key) => {
        const by = summary?.by_source || {};
        if (key === 'all') return Object.values(by).reduce((n, v) => n + (v[sourceBase] || 0), 0);
        return by[key]?.[sourceBase] || 0;
    };
    const sourceKeys = ['all', 'pitchbook', 'csv', ...(sourceCount('legacy_staging') ? ['legacy_staging'] : [])];
    const grouped = (state.tab === 'imports' || state.tab === 'staging') && state.source !== 'all';
    const showSubmenu = ['imports', 'staging', 'pool'].includes(state.tab);

    const onTabKey = (e, idx) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = (idx + (e.key === 'ArrowRight' ? 1 : -1) + TAB_SPEC.length) % TAB_SPEC.length;
        setTab(TAB_SPEC[next].key);
        tablistRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
    };

    const noSelection = selectedCount === 0 || busy;
    const needSel = busy ? 'Working…' : noSelection ? 'Select records first' : undefined;

    const loadEventEnrollContacts = useCallback(async (search = '') => {
        const response = await axios.get(`${RUNTIME_API}/contacts`, {
            params: { search: search.trim() || undefined, page_size: 50 },
            headers: getAuthHeader(),
        });
        const payload = response.data;
        setEventEnrollContacts(Array.isArray(payload) ? payload : (payload.contacts || payload.data || []));
    }, [getAuthHeader]);

    const openExistingContactEventEnrollment = async () => {
        setEventEnrollOpen(true);
        setEventEnrollEventId('');
        setEventEnrollSelected(new Set());
        setEventEnrollLoading(true);
        try {
            const [eventsResponse] = await Promise.all([
                axios.get(`${RUNTIME_API}/events`, {
                    params: { status: 'published', page_size: 100 },
                    headers: getAuthHeader(),
                }),
                loadEventEnrollContacts(''),
            ]);
            const payload = eventsResponse.data;
            setEventEnrollEvents(Array.isArray(payload) ? payload : (payload.data || payload.events || []));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not load existing contacts or events');
        } finally {
            setEventEnrollLoading(false);
        }
    };

    const enrollExistingContactsInEvent = async () => {
        if (!eventEnrollEventId || eventEnrollSelected.size === 0) return;
        setEventEnrollLoading(true);
        try {
            const selectedContacts = eventEnrollContacts.filter(contact => eventEnrollSelected.has(contact.contact_id));
            const result = await eventContactsApi.addAttendees(eventEnrollEventId, {
                contact_ids: [...eventEnrollSelected], outreach: true,
                include_outcomes: true,
                operation_id: `list-management-enroll:${Date.now()}`,
            });
            if (result.outcomes?.length) {
                eventEnrollmentOutcomeMessages(result.outcomes, selectedContacts)
                    .forEach(({ tone, message }) => toast[tone](message));
            } else {
                const added = (result.created || 0) + (result.restored || 0);
                toast.success(`${added} contact${added === 1 ? '' : 's'} added`);
            }
            setEventEnrollOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not add contacts to the event');
        } finally {
            setEventEnrollLoading(false);
        }
    };


    const toolbarActions = () => {
        if (state.tab === 'imports') {
            return (
                <>
                    {allow('delete') && <GhostButton icon={Trash2} disabled={noSelection} title={needSel} onClick={() => openBulk('delete')}>Delete</GhostButton>}
                    {allow('review') && (
                        <PrimaryButton icon={ArrowRight} disabled={noSelection} title={needSel} testId="lm-move-to-staging"
                                       onClick={() => quick('move-to-staging', 'moved to Staging', 'Move to Staging')}>
                            Move to Staging{selectedCount ? ` (${selectedCount})` : ''}
                        </PrimaryButton>
                    )}
                </>
            );
        }
        if (state.tab === 'staging') {
            const enrichDisabled = noSelection || allMatching || selected.size > ENRICH_MAX;
            return (
                <>
                    {allow('review') && (
                        <GhostButton icon={WandSparkles} disabled={enrichDisabled} onClick={() => setEnrichIds([...selected])}
                                     title={noSelection ? needSel : enrichDisabled ? `Select ${ENRICH_MAX} or fewer records on this page to enrich` : 'AI-powered analysis of the selected records'}>
                            Enrich{selected.size && !allMatching ? ` (${selected.size})` : ''}
                        </GhostButton>
                    )}
                    {allow('review') && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={noSelection}>
                                <GhostButton disabled={noSelection} title={needSel}>Resolve match</GhostButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {/* One row at a time, with its candidates and a field comparison (#772 QA):
                                    one bulk "accept" cannot be meaningful across ambiguous rows. */}
                                <DropdownMenuItem disabled={allMatching || selected.size > RESOLVE_MAX_ROWS}
                                                  onSelect={() => setResolveIds([...selected])} data-testid="lm-resolve-review">
                                    {allMatching || selected.size > RESOLVE_MAX_ROWS
                                        ? `Review matches (select ${RESOLVE_MAX_ROWS} or fewer on this page)`
                                        : `Review matches one by one (${selected.size})`}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setBulk({ kind: 'resolve', decision: 'new', selection: selectionBody(), count: selectedCount })}>Create new records</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setBulk({ kind: 'resolve', decision: 'skip', selection: selectionBody(), count: selectedCount })}>Skip (do not import)</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {allow('assign') && <GhostButton icon={UserPlus} disabled={noSelection} title={needSel} onClick={() => { setAssignOwner(''); openBulk('assign'); }}>Assign</GhostButton>}
                    {allow('delete') && <GhostButton icon={Trash2} disabled={noSelection} title={needSel} onClick={() => openBulk('delete')}>Delete</GhostButton>}
                    {allow('move_to_pool') && (
                        <PrimaryButton icon={Inbox} disabled={noSelection} title={needSel} testId="lm-move-to-pool"
                                       onClick={() => quick('move-to-pool', 'moved to Pooling', 'Move to Pool')}>
                            Move to Pool{selectedCount ? ` (${selectedCount})` : ''}
                        </PrimaryButton>
                    )}
                </>
            );
        }
        if (state.tab === 'pool') {
            return (
                <>
                    {allow('review') && (
                        <GhostButton icon={CalendarDays} testId="lm-add-existing-contacts-to-event" onClick={openExistingContactEventEnrollment}>
                            Add existing contacts to event
                        </GhostButton>
                    )}
                    {allow('assign') && <GhostButton icon={UserPlus} disabled={noSelection} title={needSel} onClick={() => { setAssignOwner(''); openBulk('assign'); }}>Assign</GhostButton>}
                    {allow('move_to_pool') && <GhostButton icon={Undo2} disabled={noSelection} title={needSel} onClick={() => quick('return-to-staging', 'returned to Staging', 'Return to Staging')}>Return to Staging</GhostButton>}
                    {allow('delete') && <GhostButton icon={Trash2} disabled={noSelection} title={needSel} onClick={() => openBulk('delete')}>Delete</GhostButton>}
                    {allow('commit') && (
                        <PrimaryButton tone="dark" disabled={noSelection} title={needSel} testId="lm-commit"
                                       onClick={() => setCommitSelection(selectionBody())}>
                            Commit selected{selectedCount ? ` (${selectedCount})` : ''}
                        </PrimaryButton>
                    )}
                </>
            );
        }
        if (state.tab === 'deleted') {
            return allow('delete') ? (
                <PrimaryButton icon={Undo2} disabled={noSelection} title={needSel}
                               onClick={() => quick('restore', 'restored', 'Restore')}>Restore{selectedCount ? ` (${selectedCount})` : ''}</PrimaryButton>
            ) : null;
        }
        return null;
    };

    const filters = () => {
        if (state.tab !== 'staging' && state.tab !== 'pool') return null;
        return (
            <div className="flex items-center gap-3 flex-wrap px-4 pb-3" data-testid="lm-filters">
                <FilterSelect label="Entity" value={state.entity} onChange={(v) => update({ entity: v })}
                              options={[['Contact', 'Contact'], ['Company', 'Company']]} />
                <FilterSelect label="Match" value={state.match} onChange={(v) => update({ match: v })}
                              options={Object.entries(MATCH_LABELS)} />
                {state.tab === 'staging' && (
                    <FilterSelect label="Validation" value={state.validation} onChange={(v) => update({ validation: v })}
                                  options={[['valid', 'Ready'], ['needs_review', 'Needs review']]} />
                )}
                <FilterSelect label="Owner" value={state.owner} onChange={(v) => update({ owner: v })}
                              options={[['unassigned', 'Unassigned'], ...owners.map((o) => [o.user_id, o.name])]} />
            </div>
        );
    };

    const bulkDialog = () => {
        if (!bulk) return null;
        const common = { open: true, onClose: () => setBulk(null), selection: bulk.selection, count: bulk.count, labelFor,
                         onSelectionChanged: selectionChanged };
        if (bulk.kind === 'delete') {
            return (
                <LmBulkDialog {...common} title="Delete records" askReason confirmLabel="Delete" tone="dark"
                              description="Deleted records move to the Deleted view and can be restored. CRM records are never deleted here."
                              run={(sel, { reason }) => runBulk('delete', sel, { reason, view })}
                              onDone={(res) => afterBulk(res, 'deleted')} />
            );
        }
        if (bulk.kind === 'assign') {
            return (
                <LmBulkDialog {...common} title="Assign owner" confirmLabel="Assign"
                              description="Pool assignment is pre-CRM: it sets who will own the records when they are committed."
                              extra={<OwnerSelect owners={owners} value={assignOwner} onChange={setAssignOwner} includeUnassign />}
                              confirmDisabled={!assignOwner} confirmHint="Choose an owner first"
                              run={(sel) => runBulk('assign', sel, { owner_id: assignOwner === '__unassign__' ? null : assignOwner })}
                              onDone={(res) => afterBulk(res, 'assigned')} />
            );
        }
        if (bulk.kind === 'resolve') {
            const copy = { new: 'Create new records instead of matching.', skip: 'Skip these records — they will not be imported.' };
            return (
                <LmBulkDialog {...common} title="Resolve matches" confirmLabel="Apply decision" description={copy[bulk.decision]}
                              run={(sel) => runBulk('resolve-match', sel, { target: 'record', decision: bulk.decision })}
                              onDone={(res) => afterBulk(res, 'updated')} />
            );
        }
        if (bulk.kind === 'outcome') {
            return (
                <BulkOutcome {...common} bulk={bulk} run={(sel) => runBulk(bulk.action, sel)} onDone={(res) => afterBulk(res, bulk.verb)} />
            );
        }
        return null;
    };

    const tabCounts = summary?.tabs || {};
    return (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-background" data-testid="list-management-page">
            <div className="shrink-0 flex items-center gap-[22px] px-4 md:px-7 pt-2.5 bg-background border-b border-border overflow-x-auto">
                <h1 className="shrink-0 inline-flex items-center h-[42px] text-[17px] font-semibold tracking-[-0.015em] text-foreground">List Management</h1>
                <div role="tablist" aria-label="List Management views" ref={tablistRef} className="flex items-center gap-[22px]">
                    {TAB_SPEC.map((t, i) => {
                        const active = state.tab === t.key;
                        const Icon = t.icon;
                        return (
                            <button key={t.key} type="button" role="tab" aria-selected={active} tabIndex={active ? 0 : -1}
                                    id={`lm-tab-${t.key}`} aria-controls="lm-panel" data-testid={`lm-tab-${t.key}`}
                                    onClick={() => setTab(t.key)} onKeyDown={(e) => onTabKey(e, i)}
                                    className={`inline-flex items-center gap-2 h-[42px] -mb-px text-[13.5px] whitespace-nowrap border-b-2 ${active ? 'text-foreground font-semibold border-primary' : 'text-ink-2 font-medium border-transparent'}`}>
                                <Icon className="w-[15px] h-[15px]" aria-hidden="true" />{t.label}
                                {t.count && <CountPill count={tabCounts[t.count] ?? 0} active={active} />}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 min-h-0 flex items-stretch">
                {showSubmenu && (
                    <nav aria-label={state.tab === 'pool' ? 'For Pooling' : 'Sources'}
                         className="hidden md:block shrink-0 w-[232px] px-3 pt-[18px] pb-12 border-r border-border bg-background overflow-y-auto">
                        <div className="px-2 pb-2 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                            {state.tab === 'pool' ? 'FOR POOLING' : 'SOURCES'}
                        </div>
                        {state.tab !== 'pool' ? (
                            <div className="flex flex-col gap-0.5">
                                {sourceKeys.map((key) => {
                                    const meta = SOURCE_META[key];
                                    const Icon = SOURCE_ICONS[meta.icon] || Layers;
                                    const on = state.source === key;
                                    const label = key === 'all' && state.tab === 'staging' ? 'All Staged' : meta.label;
                                    return (
                                        <button key={key} type="button" onClick={() => update({ source: key })} aria-current={on || undefined}
                                                data-testid={`lm-source-${key}`}
                                                className={`flex items-center gap-[9px] h-8 px-2 rounded-lg text-[12.5px] text-left ${on ? 'bg-accent text-foreground font-semibold' : 'text-ink-2 font-medium hover:bg-muted'}`}>
                                            <span className={`shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full ${toneClasses(meta)}`}>
                                                <Icon className="w-[11px] h-[11px]" aria-hidden="true" />
                                            </span>
                                            <span className="flex-1 min-w-0 truncate">{label}</span>
                                            <span className={`shrink-0 text-[11.5px] tabular-nums ${on ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>{sourceCount(key)}</span>
                                        </button>
                                    );
                                })}
                                {state.tab === 'imports' && allow('import') && (
                                    <button type="button" onClick={() => setWizardOpen(true)} data-testid="lm-open-import"
                                            className="flex items-center justify-center gap-1.5 h-[34px] mt-2.5 border border-input rounded-lg bg-card text-[12.5px] font-medium text-foreground hover:bg-muted">
                                        <Plus className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />Import
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {[['contacts', 'Contacts', Users, tabCounts.pool], ['distribution', 'Rep Distribution', Layers, null], ['balance', 'Rep Balance', Scale, null]].map(([key, label, Icon, n]) => {
                                    const on = state.sub === key;
                                    return (
                                        <button key={key} type="button" onClick={() => update({ sub: key })} aria-current={on || undefined}
                                                data-testid={`lm-pool-${key}`}
                                                className={`flex items-center gap-2.5 h-9 px-2.5 rounded-[9px] text-[13px] text-left whitespace-nowrap ${on ? 'bg-primary text-primary-foreground font-semibold' : 'text-foreground font-medium hover:bg-muted'}`}>
                                            <Icon className="w-[15px] h-[15px] shrink-0" aria-hidden="true" />
                                            <span className="flex-1 min-w-0">{label}</span>
                                            {n != null && (
                                                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums ${on ? 'bg-primary-foreground text-primary' : 'bg-muted text-ink-2'}`}>{n}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </nav>
                )}

                {/* A flex column: the table views fill it and scroll only their table
                    region (#770 QA), so the toolbar and pagination stay in view. The
                    panel's own scroll is the fallback for very short windows, where a
                    table view keeps a minimum height. */}
                <main id="lm-panel" role="tabpanel" aria-labelledby={`lm-tab-${state.tab}`}
                      className={`flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto px-4 md:px-7 pt-[18px] ${tableView ? 'pb-4' : 'pb-12'}`}>
                    {showSubmenu && (
                        <div className="md:hidden shrink-0 flex gap-2 overflow-x-auto pb-3" aria-label="Sources (compact)">
                            {state.tab !== 'pool'
                                ? sourceKeys.map((k) => (
                                    <button key={k} type="button" onClick={() => update({ source: k })}
                                            className={`shrink-0 h-8 px-3 rounded-full text-[12.5px] border ${state.source === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-input'}`}>
                                        {SOURCE_META[k].label} · {sourceCount(k)}
                                    </button>
                                ))
                                : [['contacts', 'Contacts'], ['distribution', 'Rep Distribution'], ['balance', 'Rep Balance']].map(([k, l]) => (
                                    <button key={k} type="button" onClick={() => update({ sub: k })}
                                            className={`shrink-0 h-8 px-3 rounded-full text-[12.5px] border ${state.sub === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-input'}`}>{l}</button>
                                ))}
                            {state.tab === 'imports' && allow('import') && (
                                <button type="button" onClick={() => setWizardOpen(true)} className="shrink-0 h-8 px-3 rounded-full text-[12.5px] border border-input bg-card text-foreground">+ Import</button>
                            )}
                        </div>
                    )}

                    {state.tab === 'history' && (
                        <LmHistoryView api={api} refreshKey={refreshKey} canCommit={allow('commit')} canRevert={allow('revert')}
                                       canDelete={allow('delete')} onChanged={refresh}
                                       canMigrateOwners={canMigrateOwners} canRestoreOwner={canRestoreOwner}
                                       currentUserId={user?.user_id}
                                       query={{ q: state.q, page: state.page, size: state.size, bstatus: state.bstatus, migration: state.migration }}
                                       onQueryChange={update}
                                       onOpenRows={openImportRows} />
                    )}

                    {state.tab === 'pool' && state.sub === 'distribution' && <RepDistribution api={api} refreshKey={refreshKey} />}
                    {state.tab === 'pool' && state.sub === 'balance' && (
                        <RepBalance api={api} refreshKey={refreshKey} canAssign={allow('assign')} onApplied={refresh} />
                    )}

                    {rowsTab && (
                        <div className="flex-1 min-h-[420px] min-w-[320px] flex flex-col">
                            {state.tab === 'imports' && <ReadOnlyBanner />}
                            {['imports', 'staging', 'pool'].includes(state.tab) && <NoCrmWriteBanner />}
                            {state.tab === 'deleted' && (
                                <DeletedImports api={api} refreshKey={refreshKey} search={state.q} canRestore={allow('delete')} onRestored={refresh} />
                            )}
                            {state.tab === 'deleted' && (
                                <LmRetention api={api} canEdit={allow('delete')}
                                             onChanged={(s) => {
                                                 toast.success(s.deleted_retention_days
                                                     ? `Deleted records will be purged ${s.deleted_retention_days} days after deletion`
                                                     : 'Deleted records will be kept until restored');
                                                 rows.refetch();
                                             }} />
                            )}
                            <div className="flex-1 min-h-0 flex flex-col bg-card border border-border rounded-xl overflow-hidden">
                                <div className="flex items-center gap-3 px-4 py-3.5 flex-wrap">
                                    <label className="flex items-center gap-[9px] w-[360px] max-w-full h-[38px] px-3 border border-input rounded-[9px] bg-card">
                                        <Search className={`w-[15px] h-[15px] ${DECOR_TEXT}`} aria-hidden="true" />
                                        <input value={state.q} onChange={(e) => update({ q: e.target.value })}
                                               placeholder={SEARCH_PLACEHOLDER[state.tab]} aria-label={SEARCH_PLACEHOLDER[state.tab]}
                                               data-testid="lm-search"
                                               className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground" />
                                    </label>
                                    {state.imp && (
                                        <span className="inline-flex items-center gap-1.5 h-[30px] pl-2.5 pr-1 rounded-full bg-violet-50 dark:bg-violet-500/15 text-[12.5px] text-foreground"
                                              data-testid="lm-import-filter">
                                            Import: <span className="font-semibold max-w-[220px] truncate">{importFilterLabel}</span>
                                            <button type="button" aria-label="Clear the import filter" onClick={() => update({ imp: '' })}
                                                    className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-violet-100 dark:hover:bg-violet-500/30">
                                                <X className="w-3 h-3" aria-hidden="true" />
                                            </button>
                                        </span>
                                    )}
                                    {state.tab !== 'deleted' && (
                                        <LmColumnPicker columns={allColumns} visibleIds={visibleColumnIds}
                                                        onToggle={(col, on) => applyColumnPrefs(toggleColumn(prefs, col, on))}
                                                        onSelectAll={() => applyColumnPrefs(showAllColumns(allColumns))}
                                                        onClear={() => applyColumnPrefs(clearColumns(allColumns))}
                                                        onReset={() => applyColumnPrefs(resetColumns())} />
                                    )}
                                    {state.tab !== 'deleted' && sourceColumnsCapped && (
                                        <span className="text-[12px] text-muted-foreground" role="status" data-testid="lm-source-columns-notice">
                                            Values are loaded for the first {MAX_SOURCE_COLUMNS} file columns.
                                        </span>
                                    )}
                                    <div className="flex-1" />
                                    <span className="text-[13px] text-muted-foreground tabular-nums" data-testid="lm-record-count">{rows.total.toLocaleString()} records</span>
                                    {toolbarActions()}
                                </div>
                                {filters()}
                                {(pageFullySelected || allMatching) && matchingTotal > pageSelectable.length && (
                                    <div className="px-4 py-2 bg-violet-50 dark:bg-violet-500/15 border-t border-border text-[12.5px] text-foreground" role="status" data-testid="lm-select-all-banner">
                                        {allMatching ? (
                                            <>All {matchingTotal.toLocaleString()} matching {state.tab === 'imports' ? 'importable ' : ''}records are selected.{' '}
                                                <button type="button" className="font-semibold text-violet-700 dark:text-violet-300" onClick={() => { setAllMatching(false); setSelected(new Set()); }}>Clear selection</button></>
                                        ) : (
                                            <>All {pageSelectable.length} selectable records on this page are selected.{' '}
                                                <button type="button" className="font-semibold text-violet-700 dark:text-violet-300" data-testid="lm-select-all-matching"
                                                        onClick={() => setAllMatching(true)}>Select all {matchingTotal.toLocaleString()} matching</button></>
                                        )}
                                    </div>
                                )}
                                <LmTable
                                    tab={state.tab} columns={columns} rows={rows.data} batches={rows.batches} grouped={grouped}
                                    selected={allMatching ? new Set(pageSelectable.map((r) => r.row_id)) : selected}
                                    onToggleRow={toggleRow} onToggleMany={toggleMany}
                                    sort={state.sort} order={state.order || 'desc'}
                                    onSort={(key) => update({ sort: key, order: state.sort === key && (state.order || 'desc') === 'asc' ? 'desc' : 'asc' })}
                                    onOpenRow={(row) => setDrawerRowId(row.row_id)}
                                    loading={rows.loading} status={rows.status} error={rows.error} onRetry={rows.refetch}
                                    emptyLabel={EMPTY_LABEL[state.tab]}
                                    page={state.page} pageSize={state.size} total={rows.total}
                                    onPageChange={(p) => update({ page: p }, { resetPage: false })}
                                    onPageSizeChange={(n) => update({ size: n })}
                                />
                            </div>
                            {state.tab === 'pool' && summary?.stats && (
                                <div className="mt-3 text-[12.5px] text-ink-2" data-testid="lm-pool-footer">
                                    Blocked: {summary.stats.unassigned} missing owner · In pool: {tabCounts.pool ?? 0}
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            <LmImportWizard open={wizardOpen} onClose={() => { setWizardOpen(false); loadCatalog(); }} api={api} canMigrateOwners={canMigrateOwners}
                            onImported={(imp) => { toast.success(`Imported ${imp.counts?.total ?? imp.row_count} records into ${imp.batch_name}`); update({ tab: 'imports', source: imp.source_type }); refresh(); loadCatalog(); }} />
            <LmRowDrawer open={!!drawerRowId} rowId={drawerRowId} tab={state.tab} api={api} owners={owners} fieldLabels={fieldLabels}
                         canAssign={allow('assign')} onClose={() => setDrawerRowId(null)} onChanged={refresh} />
            {commitSelection && (
                <LmCommitDialog open api={api} selection={commitSelection} canMigrateOwners={canMigrateOwners}
                                onClose={() => setCommitSelection(null)}
                                onFinished={() => { refresh(); setSelected(new Set()); setAllMatching(false); }}
                                onViewHistory={() => { setCommitSelection(null); setTab('history'); }}
                                onShowBlocked={(code, filter) => {
                                    setCommitSelection(null);
                                    update({ tab: 'pool', sub: 'contacts', owner: '', ...(filter?.patch || {}) });
                                }} />
            )}
            {resolveIds && (
                <LmResolveDialog open api={api} rowIds={resolveIds} fieldLabels={fieldLabels}
                                 onClose={() => { setResolveIds(null); refresh(); }} onChanged={() => rows.refetch()} />
            )}
            {enrichIds && (
                <LmEnrichDialog open api={api} rowIds={enrichIds} labelFor={labelFor} fieldLabels={fieldLabels}
                                onClose={() => setEnrichIds(null)} onApplied={refresh} />
            )}
            <Dialog open={eventEnrollOpen} onOpenChange={setEventEnrollOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader><DialogTitle>Add existing contacts to event</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">Only existing visible contacts are shown. Enrollment never creates a duplicate contact.</p>
                        <select
                            value={eventEnrollEventId}
                            onChange={(e) => setEventEnrollEventId(e.target.value)}
                            className="w-full rounded border border-input px-3 py-2 text-sm bg-card text-foreground"
                            aria-label="Event"
                        >
                            <option value="">— Select an event —</option>
                            {eventEnrollEvents.map((event) => (
                                <option key={event.event_id} value={event.event_id}>{event.name || event.title || event.event_id}</option>
                            ))}
                        </select>
                        <label className="flex items-center gap-2 rounded border border-input px-3 py-2">
                            <Search className={`w-4 h-4 ${DECOR_TEXT}`} aria-hidden="true" />
                            <input
                                value={eventEnrollSearch}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEventEnrollSearch(value);
                                    loadEventEnrollContacts(value);
                                }}
                                placeholder="Search contacts"
                                aria-label="Search contacts to enroll"
                                className="flex-1 min-w-0 border-0 outline-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground"
                            />
                        </label>
                        <div className="max-h-64 overflow-y-auto rounded border border-border divide-y divide-border">
                            {eventEnrollContacts.length === 0 ? (
                                <p className="px-3 py-6 text-center text-sm text-muted-foreground">No visible contacts found.</p>
                            ) : eventEnrollContacts.map((contact) => {
                                const contactId = contact.contact_id;
                                const selected = eventEnrollSelected.has(contactId);
                                return (
                                    <label key={contactId} className="flex items-center gap-3 px-3 py-2 hover:bg-muted">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => setEventEnrollSelected((previous) => {
                                                const next = new Set(previous);
                                                if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
                                                return next;
                                            })}
                                            className="rounded border-ink-3"
                                        />
                                        <span className="min-w-0 text-sm text-foreground truncate">
                                            {(contact.first_name || '') + ' ' + (contact.last_name || '')}
                                            {contact.email ? <span className="ml-2 text-xs text-muted-foreground">{contact.email}</span> : null}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" className="rounded border border-input bg-card text-foreground px-3 py-2 text-sm" onClick={() => setEventEnrollOpen(false)}>Cancel</button>
                            <button type="button" className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50" onClick={enrollExistingContactsInEvent} disabled={!eventEnrollEventId || eventEnrollSelected.size === 0 || eventEnrollLoading}>
                                {eventEnrollLoading ? 'Adding…' : `Add ${eventEnrollSelected.size || ''} to event`}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {bulkDialog()}
        </div>
    );
}

/** Partial-failure outcome for an immediate move: lists the failures, retries only them. */
function BulkOutcome({ bulk, run, onDone, ...common }) {
    const failed = (bulk.initial?.results || []).filter((r) => !r.ok);
    return (
        <LmBulkDialog {...common} title={`${bulk.title}: ${bulk.initial.succeeded} succeeded, ${bulk.initial.failed} need attention`}
                      description="Successful rows were moved. The rows below did not move — open one to see why."
                      confirmLabel={`Retry ${failed.length} failed`}
                      selection={{ row_ids: failed.map((f) => f.row_id) }} count={failed.length}
                      extra={(
                          <ul className="max-h-[240px] overflow-y-auto border border-border rounded-[10px] divide-y divide-border" data-testid="lm-bulk-failures">
                              {failed.map((f) => (
                                  <li key={f.row_id} className="px-3 py-2 text-[12.5px]">
                                      <div className="font-medium text-foreground">{common.labelFor(f.row_id)}</div>
                                      <div className="text-muted-foreground">{f.error?.codes?.length ? f.error.codes.map(blockLabel).join(', ') : f.error?.message}</div>
                                  </li>
                              ))}
                          </ul>
                      )}
                      run={run} onDone={onDone} />
    );
}
